import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useAtomValue } from 'jotai';
import { lpgLinksAtom } from '../../../../Atoms';
import renderDynamicElements from '../../Utils/renderDynamicElements';
import '../css/chip.scss';

const BASE_STYLE = {
  fontSize: '1rem',
  fontFamily: 'Roboto',
  margin: 0,
  padding: '4%',
  textAlign: 'justify',
};

const PLACEHOLDER_REGEX = /{[^}]*}/g;
const ZERO_WIDTH_SPACE = '\u200B';

// content is plain text where <br> is the only markup, as the output renders it
function toEditorHtml(content, intl, lpgLinks) {
  const escaped = String(content ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replace(/&lt;br\s*\/?&gt;/gi, '<br>');
  return renderDynamicElements(escaped, intl, lpgLinks);
}

// reads the editor DOM back into content: chips become {placeholders}, line breaks become <br> (or \n)
function serialize(editor, plainTextOnly) {
  const lineBreak = plainTextOnly ? '\n' : '<br>';
  const clone = editor.cloneNode(true);
  clone
    .querySelectorAll('.chip')
    .forEach((chip) => chip.replaceWith(`{${chip.dataset.entity}}`));
  clone.querySelectorAll('br').forEach((br) => br.replaceWith(lineBreak));
  clone
    .querySelectorAll('div, p')
    .forEach((block) => block.previousSibling && block.before(lineBreak));
  return clone.textContent
    .replaceAll(ZERO_WIDTH_SPACE, '')
    .replaceAll('\u00A0', ' ');
}

// replaces the selection with the given nodes and puts the caret after them
function insertAtCaret(editor, nodes) {
  const selection = window.getSelection();
  if (!selection.rangeCount || !editor.contains(selection.anchorNode))
    return false;
  const range = selection.getRangeAt(0);
  range.deleteContents();
  const fragment = document.createDocumentFragment();
  nodes.forEach((node) => fragment.appendChild(node));
  const last = fragment.lastChild;
  range.insertNode(fragment);
  range.setStartAfter(last);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
}

const EditorContent = function EditorContent({
  id,
  edit = false,
  content = '',
  onChange = () => {},
  plainTextOnly = false,
  style = {},
  templateName = '',
  dynamicElement = null,
  ...props
}) {
  const intl = useIntl();
  const editorRef = useRef(null);
  const debounceTimeout = useRef(null); //handles the debounce time
  const lpgLinks = useAtomValue(lpgLinksAtom);

  const save = () => {
    const newContent = serialize(editorRef.current, plainTextOnly);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      onChange({
        id,
        ...props,
        style,
        content: newContent,
      });
    }, 600);
  };

  const lineBreakNodes = () => [
    plainTextOnly
      ? document.createTextNode('\n')
      : document.createElement('br'),
    document.createTextNode(ZERO_WIDTH_SPACE),
  ];

  const chipNodes = (value) => {
    const template = document.createElement('template');
    template.innerHTML = toEditorHtml(value, intl, lpgLinks);
    return Array.from(template.content.childNodes);
  };

  const onKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (insertAtCaret(editorRef.current, lineBreakNodes())) save();
  };

  const onPaste = (e) => {
    e.preventDefault();
    const nodes = e.clipboardData
      .getData('text/plain')
      .split(/\r?\n/)
      .flatMap((line, index) => [
        ...(index > 0 ? lineBreakNodes() : []),
        document.createTextNode(line),
      ]);
    if (insertAtCaret(editorRef.current, nodes)) save();
  };

  const onClick = (e) => {
    const chip = e.target.closest('.closeBtn')?.closest('.chip');
    if (!chip) return;
    chip.remove();
    save();
  };

  // placeholders typed by hand become chips once the editor loses focus
  const onBlur = () => {
    const editor = editorRef.current;
    const text = serialize(editor, plainTextOnly);
    const placeholders = text.match(PLACEHOLDER_REGEX)?.length ?? 0;
    if (placeholders !== editor.querySelectorAll('.chip').length)
      editor.innerHTML = toEditorHtml(text, intl, lpgLinks);
  };

  useEffect(() => {
    //sets the default value on the contenteditable div
    if (edit && editorRef.current)
      editorRef.current.innerHTML = toEditorHtml(content, intl, lpgLinks);
  }, [templateName]);

  useEffect(() => {
    if (
      dynamicElement &&
      editorRef.current &&
      insertAtCaret(editorRef.current, chipNodes(dynamicElement.value))
    )
      save();
  }, [dynamicElement]);

  return edit ? (
    <div
      id={id}
      ref={editorRef}
      className="editor"
      style={plainTextOnly ? style : { ...BASE_STYLE, ...style }}
      contentEditable
      onInput={save}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      onClick={onClick}
      onBlur={onBlur}
      suppressContentEditableWarning
      data-placeholder={intl.messages.CONTENT}
    />
  ) : (
    <p id={id} style={{ ...BASE_STYLE, ...style }}>
      {String(content)
        .split(/<br\s*\/?>/i)
        .map((line, index) => (
          <React.Fragment key={String(index)}>
            {index > 0 && <br />}
            {line}
          </React.Fragment>
        ))}
    </p>
  );
};

EditorContent.propTypes = {
  id: PropTypes.string.isRequired,
  edit: PropTypes.bool,
  onChange: PropTypes.func,
  content: PropTypes.string,
  plainTextOnly: PropTypes.bool,
  style: PropTypes.object,
  templateName: PropTypes.string,
  dynamicElement: PropTypes.object,
};

export default EditorContent;
